import { allocateFlags } from '../allocate.js';
import { localePack } from '../locale/registry.js';
import { seededRng } from '../rng.js';
import { containsExternalContact } from './contact-guard.js';
import type { MarketLocale } from '../../apps.js';
import type { BioTopic, LocalePack, ZodiacSign } from '../locale/types.js';
import type { Rng } from '../rng.js';

/**
 * Bios that read like the app's own users wrote them.
 *
 * What makes a generated profile look fake is sameness: every bio filled in,
 * every bio a polished quote, the same emoji set, the same shape. So a share of
 * profiles get no bio at all; most bios are one short fragment; topics follow
 * what each gender tends to mention; a bio agrees with its profile (the zodiac
 * sign from the birth date, hobbies from the chosen interests); typing habits
 * are applied per bio; and no bio skeleton repeats more than twice in any 20.
 * No bio may carry an off-app contact (`containsExternalContact`).
 */

export type BioRegister = 'plain' | 'sincere' | 'funny' | 'catchy' | 'cringe' | 'emoji-list';

export interface BioGenConfig {
  registers: BioRegister[];
  /** Share of profiles that have a bio at all. Real apps sit well below 1. */
  fillRatio: number;
  emoji: boolean;
}

export interface BioSubject {
  gender: 'male' | 'female';
  /** YYYY-MM-DD */
  birthDate: string;
  /** Names of the profile's chosen interests, preferred for the hobby slot. */
  interestNames: string[];
}

export function defaultBioGenConfig(): BioGenConfig {
  return { registers: ['plain', 'sincere', 'funny', 'catchy'], fillRatio: 0.6, emoji: true };
}

const EMOJI_RATE = { female: 0.4, male: 0.34 } as const;
/** Base topic weights before the pack's gender bias; one-liners carry most bios. */
const BASE_TOPIC_WEIGHT: Readonly<Record<BioTopic, number>> = {
  oneLiner: 4, hobby: 2, zodiac: 1, team: 1, school: 1, job: 1, lyric: 1, height: 1, pet: 1,
};
const TOPICS = Object.keys(BASE_TOPIC_WEIGHT) as BioTopic[];
const SKELETON_WINDOW = 20;
const SKELETON_MAX = 2;

export function generateBios(
  config: BioGenConfig,
  subjects: BioSubject[],
  locale: MarketLocale,
  seed?: string | null,
  pack: LocalePack = localePack(locale),
): (string | null)[] {
  return generateBioDetails(pack, config, subjects, seed).map((d) => d.bio);
}

export function generateBioDetails(
  pack: LocalePack,
  config: BioGenConfig,
  subjects: BioSubject[],
  seed?: string | null,
): { bio: string | null; skeleton: string | null }[] {
  const rng = seededRng(seed);
  const filled = allocateFlags(subjects.length, config.fillRatio, rng);
  const emojiFor = emojiAllocation(subjects, config.emoji, rng);

  const recent: string[] = [];
  const seenBios = new Set<string>();
  const emojiSets = new Map<string, number>();

  return subjects.map((s, i) => {
    if (filled[i] !== true) return { bio: null, skeleton: null };

    // The shape is chosen once: retrying it too would push rejected bios
    // toward the rarer multi-line shapes, whose skeletons repeat less.
    const roll = rng.next();
    const parts = roll < 0.7 ? 1 : roll < 0.95 ? 2 + Math.floor(rng.next() * 2) : 4;
    let chosen: Composed | null = null;
    for (let attempt = 0; attempt < 24 && chosen === null; attempt++) {
      const c = compose(pack, config, s, parts, rng);
      if (countIn(recent, c.skeleton) >= SKELETON_MAX) continue;
      if (seenBios.has(c.text) || containsExternalContact(c.text)) continue;
      chosen = c;
    }
    chosen ??= fallback(pack, s, recent, rng);

    let bio = imperfect(chosen.text, pack, rng);
    if (emojiFor[i] === true) bio = withEmoji(bio, pack, s.gender, emojiSets, rng);

    recent.push(chosen.skeleton);
    if (recent.length > SKELETON_WINDOW - 1) recent.shift();
    seenBios.add(chosen.text);
    return { bio, skeleton: chosen.skeleton };
  });
}

/** Western zodiac sign for a YYYY-MM-DD date. */
export function zodiacOf(birthDate: string): ZodiacSign {
  const [, mm = '1', dd = '1'] = birthDate.split('-');
  const md = Number(mm) * 100 + Number(dd);
  if (md >= 321 && md <= 419) return 'aries';
  if (md >= 420 && md <= 520) return 'taurus';
  if (md >= 521 && md <= 620) return 'gemini';
  if (md >= 621 && md <= 722) return 'cancer';
  if (md >= 723 && md <= 822) return 'leo';
  if (md >= 823 && md <= 922) return 'virgo';
  if (md >= 923 && md <= 1022) return 'libra';
  if (md >= 1023 && md <= 1121) return 'scorpio';
  if (md >= 1122 && md <= 1221) return 'sagittarius';
  if (md >= 1222 || md <= 119) return 'capricorn';
  if (md >= 120 && md <= 218) return 'aquarius';
  return 'pisces';
}

interface Composed {
  text: string;
  skeleton: string;
}

/** `parts` fragments (1 ≈70%, 2-3 ≈25%, 4 ≈5%) from gender-biased topics. */
function compose(pack: LocalePack, config: BioGenConfig, s: BioSubject, parts: number, rng: Rng): Composed {
  const bias = pack.bio.genderTopicBias[s.gender];
  const weights = TOPICS.map((t) => ({ t, w: BASE_TOPIC_WEIGHT[t] * (bias[t] ?? 1) }));

  const used = new Set<BioTopic>();
  const lines: string[] = [];
  const keys: string[] = [];
  for (let p = 0; p < parts; p++) {
    const available = weights.filter((x) => !used.has(x.t));
    if (available.length === 0) break;
    const topic = weighted(available, rng).t;
    used.add(topic);
    const { text, key } = fragment(pack, config, topic, s, rng);
    lines.push(text);
    keys.push(key);
  }
  return { text: lines.join('\n'), skeleton: keys.join('|') };
}

function fragment(
  pack: LocalePack,
  config: BioGenConfig,
  topic: BioTopic,
  s: BioSubject,
  rng: Rng,
): { text: string; key: string } {
  const tpls = pack.bio.topicTemplates[topic];
  const ti = Math.floor(rng.next() * tpls.length);
  let key = `${topic}#${String(ti)}`;
  let value: string;
  if (topic === 'oneLiner') {
    const line = oneLiner(pack, config, s, rng);
    value = line.text;
    key += `:${line.key}`;
  } else {
    value = slotValue(pack, topic, s, rng);
  }
  return { text: (tpls[ti] ?? `{${topic}}`).replace(`{${topic}}`, value).replace(/\{(\w+)\}/g, (_, k: string) => fill(pack, k, s, rng)), key };
}

function oneLiner(pack: LocalePack, config: BioGenConfig, s: BioSubject, rng: Rng): { text: string; key: string } {
  const regs = config.registers.length > 0 ? config.registers : (['plain'] as BioRegister[]);
  const reg = pick(regs, rng);
  if (reg === 'plain' || reg === 'emoji-list') {
    const pool = [...preferInterests(s, pack.bio.hobbies, rng, 3), ...pack.bio.traits];
    const items = pickN(pool, 2 + Math.floor(rng.next() * 2), rng).map((x) => pack.lower(x));
    const sep = pick([', ', ' · ', ' ', ' | '], rng);
    return { text: items.join(sep), key: `${reg}${sep.trim() || 'sp'}${String(items.length)}` };
  }
  const bank = pack.bio.oneLiners[reg];
  const li = Math.floor(rng.next() * bank.length);
  return { text: (bank[li] ?? '').replace(/\{(\w+)\}/g, (_, k: string) => fill(pack, k, s, rng)), key: `${reg}${String(li)}` };
}

function slotValue(pack: LocalePack, topic: Exclude<BioTopic, 'oneLiner'>, s: BioSubject, rng: Rng): string {
  const b = pack.bio;
  switch (topic) {
    case 'zodiac':
      return b.zodiac[zodiacOf(s.birthDate)];
    case 'team':
      return pick(b.teams, rng);
    case 'school':
      return pick(b.schools, rng);
    case 'job':
      return pick(b.jobs, rng);
    case 'lyric':
      return pick(b.lyricsOrQuotes, rng);
    case 'pet':
      return pick(b.pets, rng);
    case 'height': {
      const [lo, hi] = s.gender === 'female' ? [155, 178] : [168, 195];
      return b.formatHeight(lo + Math.floor(rng.next() * (hi - lo + 1)));
    }
    default:
      return hobby(pack, s, rng);
  }
}

/** Slots inside one-liners and templates. */
function fill(pack: LocalePack, slot: string, s: BioSubject, rng: Rng): string {
  switch (slot) {
    case 'hobby':
      return hobby(pack, s, rng);
    case 'city':
      return weighted(pack.bio.cities.map((c) => ({ t: c.name, w: c.weight })), rng).t;
    case 'trait':
      return pick(pack.bio.traits, rng);
    case 'zodiac':
    case 'team':
    case 'school':
    case 'job':
    case 'lyric':
    case 'pet':
    case 'height':
      return slotValue(pack, slot, s, rng);
    default:
      return '';
  }
}

/** The profile's own interests win the hobby slot most of the time. */
function hobby(pack: LocalePack, s: BioSubject, rng: Rng): string {
  if (s.interestNames.length > 0 && rng.next() < 0.75) return pack.lower(pick(s.interestNames, rng));
  return pick(pack.bio.hobbies, rng);
}

function preferInterests(s: BioSubject, hobbies: string[], rng: Rng, n: number): string[] {
  return s.interestNames.length > 0 && rng.next() < 0.75 ? s.interestNames.slice(0, n) : pickN(hobbies, n, rng);
}

/** A single fragment whose skeleton is not yet at its cap — always exists for a valid pack. */
function fallback(pack: LocalePack, s: BioSubject, recent: string[], rng: Rng): Composed {
  for (const topic of TOPICS.filter((t) => t !== 'oneLiner')) {
    const tpls = pack.bio.topicTemplates[topic];
    for (let ti = 0; ti < tpls.length; ti++) {
      const key = `${topic}#${String(ti)}`;
      if (countIn(recent, key) >= SKELETON_MAX) continue;
      const text = (tpls[ti] ?? '').replace(`{${topic}}`, slotValue(pack, topic, s, rng)).replace(/\{(\w+)\}/g, (_, k: string) => fill(pack, k, s, rng));
      if (!containsExternalContact(text)) return { text, skeleton: key };
    }
  }
  return { text: pick(pack.bio.oneLiners.sincere, rng), skeleton: 'fallback' };
}

/** Typing habits, per bio: shortcuts, all-lowercase, dropped diacritics, no final stop. */
function imperfect(text: string, pack: LocalePack, rng: Rng): string {
  const im = pack.bio.imperfections;
  let out = text;
  if (rng.next() < 0.35) for (const [re, to] of im.substitutions) out = out.replace(re, to);
  if (rng.next() < im.lowercaseRate) out = pack.lower(out);
  if (rng.next() < im.dropDiacriticsRate) out = pack.asciiFold(out);
  if (rng.next() < im.noFinalPunctRate) out = out.replace(/[.!]+$/u, '');
  return out;
}

/** Exact per-gender emoji shares over the batch. */
function emojiAllocation(subjects: BioSubject[], on: boolean, rng: Rng): boolean[] {
  const out = subjects.map(() => false);
  if (!on) return out;
  for (const g of ['female', 'male'] as const) {
    const idx = subjects.flatMap((s, i) => (s.gender === g ? [i] : []));
    const flags = allocateFlags(idx.length, EMOJI_RATE[g], rng);
    idx.forEach((i, k) => {
      out[i] = flags[k] === true;
    });
  }
  return out;
}

/** 1-3 emoji attached to the text; no exact set used more than twice per batch. */
function withEmoji(
  bio: string,
  pack: LocalePack,
  gender: 'male' | 'female',
  sets: Map<string, number>,
  rng: Rng,
): string {
  const pool = [...pack.bio.emojis.general, ...pack.bio.emojis[gender], ...pack.bio.emojis[gender]];
  let chosen: string[] = [];
  for (let attempt = 0; attempt < 10; attempt++) {
    const r = rng.next();
    const n = r < 0.6 ? 1 : r < 0.9 ? 2 : 3;
    chosen = pickN([...new Set(pool)], n, rng);
    if ((sets.get([...chosen].sort().join('')) ?? 0) < 2) break;
  }
  const key = [...chosen].sort().join('');
  sets.set(key, (sets.get(key) ?? 0) + 1);
  const lines = bio.split('\n');
  const at = lines.length > 1 && rng.next() < 0.3 ? 0 : lines.length - 1;
  lines[at] = `${lines[at] ?? ''} ${chosen.join('')}`;
  return lines.join('\n');
}

function countIn(xs: string[], x: string): number {
  return xs.reduce((n, y) => (y === x ? n + 1 : n), 0);
}

function pick<T>(arr: readonly T[], rng: Rng): T {
  return arr[Math.floor(rng.next() * arr.length)] as T;
}

function pickN<T>(arr: readonly T[], n: number, rng: Rng): T[] {
  const pool = [...arr];
  const out: T[] = [];
  for (let i = 0; i < Math.min(n, pool.length); i++) out.push(pool.splice(Math.floor(rng.next() * pool.length), 1)[0] as T);
  return out;
}

function weighted<T extends { w: number }>(xs: readonly T[], rng: Rng): T {
  const total = xs.reduce((s, x) => s + x.w, 0);
  let r = rng.next() * total;
  for (const x of xs) {
    r -= x.w;
    if (r < 0) return x;
  }
  return xs[xs.length - 1]!;
}
